import React, { useState, useRef, useEffect } from "react";
import "./rack_location.css";
import { Button, Table, Input, Space, message, Popover } from "antd";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type { InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FaPlusCircle } from "react-icons/fa";
import { RiFileCloseFill } from "react-icons/ri";
import { HiOutlineDocumentCheck } from "react-icons/hi2";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import useSWR from "swr";

const RackLocation: React.FC = () => {
    const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };
    const Submitbtn = HiOutlineDocumentCheck as React.ComponentType<any>;
    const Deletebtn = RiFileCloseFill as React.ComponentType<any>;
    const { userData } = useAuth();
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [searchText, setSearchText] = useState("");
    const searchInput = useRef<InputRef>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);

    const fetchLocations = async (page = 1, size = 10, sField?: string, sOrder?: string, locSearch?: string) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/rack-location`, {
                params: {
                    warehouse_code: userData?.warehouse_code,
                    page,
                    pageSize: size,
                    sortField: sField,
                    sortOrder: sOrder,
                    locationCode: locSearch !== undefined ? locSearch : searchText,
                }
            });
            const { data, total } = res.data;
            const formatted = data.map((item: any, idx: number) => {
                return {
                    key: item.id,
                    no: (page - 1) * size + idx + 1,
                    locationCode: item.code_rack,
                    qty: Number(item.qty || 0),
                    warehouse: item.warehouse_code,
                    items: item.items || [],
                    isNew: false
                };
            });
            setDataSource(formatted);
            setTotal(total);
        } catch (err) {
            message.error("Failed to load locations.");
        }
    };

    useEffect(() => {
        if (userData?.warehouse_code) {
            fetchLocations(currentPage, pageSize, sortField, sortOrder, searchText);
        }
    }, [userData, currentPage, pageSize, sortField, sortOrder]);

    const handleNew = () => {
        const newKey = `temp-${Date.now()}`;
        const newRow = {
            key: newKey,
            no: 1,
            locationCode: "",
            qty: 0,
            warehouse: userData?.warehouse_code || "-",
            isNew: true
        };
        const updatedDataSource = dataSource.map((item, index) => ({
            ...item,
            no: (currentPage - 1) * pageSize + index + 2
        }));
        setDataSource([newRow, ...updatedDataSource]);
    };

    const handleLocationCodeChange = (val: string, key: React.Key) => {
        const updated = dataSource.map(item => {
            if (item.key === key) {
                return { ...item, locationCode: val };
            }
            return item;
        });
        setDataSource(updated);
    };

    const handleSubmit = async () => {
        const unsaved = dataSource.filter(item => item.isNew && item.locationCode.trim());
        if (unsaved.length === 0) {
            message.warning("No dynamic entries found!");
            return;
        }
        try {
            const payloads = unsaved.map(item => ({
                code_rack: item.locationCode,
                warehouse_code: userData?.warehouse_code || ""
            }));
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/rack-location`, { locations: payloads });
            message.success("Successfully registered location codes!");
            setSelectedRowKeys([]);
            fetchLocations();
        } catch (err) {
            message.error("Failed to commit locations.");
        }
    };

    const handleDelete = async () => {
        const tempKeys = selectedRowKeys.filter(k => typeof k === 'string' && k.startsWith('temp-'));
        const persistKeys = selectedRowKeys.filter(k => typeof k === 'number');
        if (tempKeys.length > 0) {
            setDataSource(dataSource.filter(item => !tempKeys.includes(item.key)));
        }
        try {
            if (persistKeys.length > 0) {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/rack-location/delete`, { ids: persistKeys });
            }
            message.success("Locations deleted successfully.");
            setSelectedRowKeys([]);
            fetchLocations();
        } catch (err) {
            message.error("Failed to delete locations.");
        }
    };

    const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => {
                        setSearchText(selectedKeys[0] as string || "");
                        fetchLocations(1, pageSize, sortField, sortOrder, selectedKeys[0] as string || "");
                        confirm();
                    }}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSearchText(selectedKeys[0] as string || "");
                            fetchLocations(1, pageSize, sortField, sortOrder, selectedKeys[0] as string || "");
                            confirm();
                        }}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters && clearFilters();
                            setSearchText("");
                            fetchLocations(1, pageSize, sortField, sortOrder, "");
                            confirm();
                        }}
                        size="small"
                        style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
    });

    const columns: ColumnsType<any> = [
        { title: "No", dataIndex: "no", key: "no", width: "50px" },
        {
            title: "Location Code",
            dataIndex: "locationCode",
            key: "locationCode",
            sorter: true,
            render: (text, record) => {
                if (record.isNew) {
                    return (
                        <Input
                            value={text}
                            onChange={(e) => handleLocationCodeChange(e.target.value, record.key)}
                        />
                    );
                }
                const popoverContent = (
                    <div style={{ minWidth: 160, fontFamily: "monospace" }}>
                        <div style={{ fontWeight: "bold", borderBottom: "1px solid #f0f0f0", paddingBottom: 4, marginBottom: 4 }}>
                            Item : qty
                        </div>
                        {record.items && record.items.length > 0 ? (
                            record.items.map((item: any, idx: number) => (
                                <div key={`${item.item_code}-${idx}`} style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                                    <span>{item.item_code}</span>
                                    <span>: {item.qty}</span>
                                </div>
                            ))
                        ) : (
                            <span style={{ color: "#8c8c8c" }}>No items</span>
                        )}
                    </div>
                );
                return (
                    <Popover content={popoverContent} getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}>
                        <div style={{ width: "100%", height: "100%", display: "block" }}>{text}</div>
                    </Popover>
                );
            },
            ...getColumnSearchProps("locationCode"),
        },
        { title: "Qty", dataIndex: "qty", key: "qty", sorter: true, ...getColumnSearchProps("qty") },
        {
            title: "Warehouse",
            dataIndex: "warehouse",
            key: "warehouse",
            sorter: true,
            ...getColumnSearchProps("warehouse"),
        },
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
        setSortField(sorter.field);
        setSortOrder(sorter.order);
    };

    return (
        <div id="iv6s" style={transformStyle}>
            <div className="gjs-grid-row" id="id04s">
                <div className="gjs-grid-column" id="i4g36">
                    <Button className="gr-button" onClick={handleNew}>
                        <FaPlusCircle style={{ width: "1em", height: "1em", marginRight: "8px" }} />
                        New
                    </Button>
                    <Button className="gr-button" onClick={handleSubmit}>
                        <Submitbtn style={{ width: "1em", height: "1em", marginRight: "8px" }} />
                        Submit
                    </Button>
                    <Button className="gr-button" onClick={handleDelete} disabled={selectedRowKeys.length === 0}>
                        <Deletebtn style={{ width: "1em", height: "1em", marginRight: "8px" }} />
                        Delete
                    </Button>
                </div>
            </div>

            <div className="gjs-grid-row" id="ih353">
                <div className="gjs-grid-column" id="ie943">
                    <Table
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (keys) => setSelectedRowKeys(keys)
                        }}
                        columns={columns}
                        dataSource={dataSource}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: total,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (totalCount, range) => `${range[0]}-${range[1]} of ${totalCount} items`,
                        }}
                        onChange={handleTableChange}
                        size="small"
                        bordered
                    />
                </div>
            </div>
        </div>
    );
};

export default RackLocation;
